import {useDispatch, useSelector} from 'react-redux'
import {useCallback, useEffect, useRef} from 'react'
import {setCapturedImage} from '../../redux/actions'
import {VIDEO_REDUCER} from '../../redux/consts'
import {CAPTURED_IMAGE} from '../../redux/video/consts'

let _stream = null
let _video = null
let _canvas = null
let _ctx = null
let _readyPromise = null

export const stopCamera = () => {
  if (_stream) {
    _stream.getTracks().forEach(t => t.stop())
  }
  _stream = null
  _video = null
  _canvas = null
  _ctx = null
  _readyPromise = null
}

// gpt0217: expose the live stream (don’t put frames in Redux)
export const getCameraStream = async () => {
  await ensureCameraReady()
  return _stream
}

// gpt0217: attach stream to a visible <video> element for live preview
export const attachCameraToVideoEl = async (videoEl) => {
  if (!videoEl) return
  await ensureCameraReady()

  // keep React element in sync with our singleton stream
  if (videoEl.srcObject !== _stream) {
    videoEl.srcObject = _stream
  }
  videoEl.playsInline = true
  videoEl.muted = true

  // play() can throw if not user-gesture; ignore and let it retry on interaction
  try { await videoEl.play() } catch (e) { /* noop */ }
}

// gpt0217: detach helper so disabling camera clears the rectangle immediately
export const detachCameraFromVideoEl = (videoEl) => {
  if (!videoEl) return
  try { videoEl.pause() } catch (e) { /* noop */ }
  videoEl.srcObject = null
}

async function loadImg(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = dataUrl
  })
}
// returns { score0to255, fracOverPxThreshold0to1 }
export async function diffScore(prevDataUrl, nextDataUrl, {
  w = 96,
  h = 72,
  perPixelThreshold = 12,   // ignore tiny jitter/noise
} = {}) {
  const [a, b] = await Promise.all([loadImg(prevDataUrl), loadImg(nextDataUrl)])

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })

  // draw prev
  ctx.clearRect(0, 0, w, h)
  ctx.drawImage(a, 0, 0, w, h)
  const A = ctx.getImageData(0, 0, w, h).data

  // draw next
  ctx.clearRect(0, 0, w, h)
  ctx.drawImage(b, 0, 0, w, h)
  const B = ctx.getImageData(0, 0, w, h).data

  let sum = 0
  let over = 0
  const n = w * h

  // grayscale-ish luma + abs diff
  for (let i = 0; i < A.length; i += 4) {
    const aY = (A[i] * 0.299 + A[i + 1] * 0.587 + A[i + 2] * 0.114)
    const bY = (B[i] * 0.299 + B[i + 1] * 0.587 + B[i + 2] * 0.114)
    const d = Math.abs(aY - bY)
    sum += d
    if (d >= perPixelThreshold) over += 1
  }

  return {
    score0to255: sum / n,
    fracOverPxThreshold0to1: over / n,
  }
}


async function ensureCameraReady() {
  if (_readyPromise) return _readyPromise

  _readyPromise = (async () => {
    _stream = await navigator.mediaDevices.getUserMedia({
      video: {
        // keep it reasonable; high-res often increases startup latency
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 30 },
      }
    })

    _video = document.createElement("video")
    _video.playsInline = true
    _video.muted = true
    _video.srcObject = _stream

    await new Promise((resolve, reject) => {
      _video.onloadedmetadata = resolve
      _video.onerror = reject
    })

    await _video.play()

    _canvas = document.createElement("canvas")
    _canvas.width = _video.videoWidth || 640
    _canvas.height = _video.videoHeight || 480
    _ctx = _canvas.getContext("2d")

    // Wait for a real frame (better than fixed sleep)
    if (_video.requestVideoFrameCallback) {
      await new Promise((resolve) => _video.requestVideoFrameCallback(() => resolve()))
    } else {
      // fallback: next paint
      await new Promise(requestAnimationFrame)
    }
  })()

  return _readyPromise
}


export const captureFromCamera = async () => {
  await ensureCameraReady()

  // keep canvas in sync if camera negotiated a different size later
  const w = _video.videoWidth || _canvas.width
  const h = _video.videoHeight || _canvas.height
  if (_canvas.width !== w) _canvas.width = w
  if (_canvas.height !== h) _canvas.height = h

  _ctx.drawImage(_video, 0, 0, _canvas.width, _canvas.height)
  return _canvas.toDataURL("image/jpeg", 0.85)
}

const useCapturePhoto = () => {
  const dispatch = useDispatch()
  const prevCapturedImage = useSelector(state => state.getIn([VIDEO_REDUCER, CAPTURED_IMAGE]))
  const prevCapturedImageRef = useRef(prevCapturedImage)

  useEffect(() => {
    prevCapturedImageRef.current = prevCapturedImage
  }, [prevCapturedImage])

  const capturePhotoToStore = useCallback(async ({toStore = true, onResult = null} = {}) => {
    console.log('[capturePhoto] called', { toStore })
    try {
      const imageUrl = await captureFromCamera()
      if (toStore) {
        dispatch(setCapturedImage(imageUrl))
      }
      return imageUrl
    } catch (err) {
      console.error("An error occurred:", err);
    }
  }, [dispatch])

  return [capturePhotoToStore, stopCamera]

};

export default useCapturePhoto;
